import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import {
  passkeyRegisterOptionsFn,
  passkeyRegisterVerifyFn,
  passkeyLoginOptionsFn,
  passkeyLoginVerifyFn,
  ClubSignInResult,
} from "../firebase";

// ═══════════════════════════════════════════════════════════════
// PASSKEY — asiakaspuoli
//
// Kaikki tarkistus tehdään palvelimella (functions/src/passkey.ts).
// Tämä moduuli vain välittää: hakee haasteen, antaa sen laitteelle
// allekirjoitettavaksi ja lähettää vastauksen takaisin.
//
// Natiiviapissa WKWebView ei tunne navigator.credentials-rajapintaa,
// joten @capgo/capacitor-passkey paikkaa sen natiiveihin rajapintoihin.
// Paikkaus tehdään kerran käynnistyksessä; sen jälkeen sama koodi
// toimii sekä selaimessa että apissa.
// ═══════════════════════════════════════════════════════════════

const RP_ORIGIN = "https://spinloop.app";

let shimReady: Promise<void> | null = null;

/**
 * Paikkaa navigator.credentials natiivialustoilla. Kutsutaan ennen
 * jokaista passkey-operaatiota; itse paikkaus tehdään vain kerran.
 */
async function ensureShim(): Promise<void> {
  if (shimReady) return shimReady;
  shimReady = (async () => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform()) return;
      const { CapacitorPasskey } = await import("@capgo/capacitor-passkey");
      await CapacitorPasskey.autoShimWebAuthn({ origin: RP_ORIGIN });
    } catch {
      // Paikkaus ei onnistunut — passkeySupported() kertoo sen kutsujalle
      // eikä nappia näytetä. Kirjautuminen PIN-koodilla toimii yhä.
    }
  })();
  return shimReady;
}

/**
 * Onko passkey käytettävissä tällä laitteella. Tätä on kysyttävä ennen
 * kuin biometrianappia näytetään: rikkinäinen nappi on huonompi kuin
 * puuttuva nappi.
 */
export async function passkeySupported(): Promise<boolean> {
  await ensureShim();
  return browserSupportsWebAuthn();
}

/**
 * Laitteen WebAuthn-virheet ovat DOMExceptioneita, joilla on `name` mutta
 * ei Firebasen `code`-kenttää. LoginFlow'n `signInError` tunnisti vain
 * koodit, joten jokainen laitevirhe näytti käyttäjälle tekstin
 * "Verkkovirhe" — riippumatta siitä mitä oikeasti tapahtui. Virheen nimi
 * on suluissa mukana, koska se on ainoa asia jonka perusteella vian voi
 * jäljittää käyttäjän kuvakaappauksesta.
 */
export function passkeyErrorMessage(err: unknown, lang: "fi" | "en"): string | null {
  const name = (err as { name?: string })?.name || "";
  const fi: Record<string, string> = {
    NotAllowedError:
      "Laite ei antanut lupaa. Peruutitko, vai ehtikö aika loppua? Jos et " +
      "ole vielä luonut passkeytä tällä laitteella, kirjaudu PIN-koodilla.",
    SecurityError:
      "Laite ei hyväksy tunnistetta tälle sovellukselle. Kyse on sovelluksen " +
      "allekirjoituksen ja palvelimen assetlinks.json-tiedoston vastaavuudesta.",
    InvalidStateError: "Tälle tilille on jo passkey tällä laitteella.",
    NotSupportedError: "Laite ei tue passkeytä.",
    ConstraintError:
      "Laite vaatii näytönlukituksen tai biometrisen tunnistuksen. Ota se " +
      "käyttöön puhelimen asetuksista ja yritä uudelleen.",
    AbortError: "Toiminto keskeytyi.",
    UnknownError: "Laitteen tunnistautuminen epäonnistui tuntemattomasta syystä.",
  };
  const en: Record<string, string> = {
    NotAllowedError:
      "The device declined. Did you cancel, or did it time out? If you have " +
      "not created a passkey on this device yet, sign in with your PIN.",
    SecurityError:
      "The device will not accept a credential for this app. This is about " +
      "the app signature matching the server's assetlinks.json.",
    InvalidStateError: "This account already has a passkey on this device.",
    NotSupportedError: "This device does not support passkeys.",
    ConstraintError:
      "The device requires a screen lock or biometrics. Turn it on in your " +
      "phone settings and try again.",
    AbortError: "The operation was cancelled.",
    UnknownError: "The device could not authenticate, for an unknown reason.",
  };
  const table = lang === "fi" ? fi : en;
  if (!name || !(name in table)) return null;
  return `${table[name]} (${name})`;
}

/** Rekisteröinnin lopputulos. `error` on käyttäjälle näytettävä teksti. */
export interface PasskeyRegisterResult {
  ok: boolean;
  error?: string;
}

/**
 * Rekisteröi passkeyn kirjautuneelle pelaajalle. Epäonnistuminen ei estä
 * kirjautumista — PIN toimii yhä — mutta se ei enää katoa jäljettömiin.
 *
 * Aiemmin tässä oli paljas `catch { return false }`, joten laitteen
 * kertoma syy hävisi kokonaan eikä kukaan saanut tietää miksi Androidilla
 * ei syntynyt yhtään passkeytä.
 */
export async function registerPasskey(
  playerId: string,
  playerName: string
): Promise<PasskeyRegisterResult> {
  if (!(await passkeySupported())) {
    return { ok: false };
  }
  try {
    const opts = await passkeyRegisterOptionsFn({ playerId, playerName });
    const response = await startRegistration({
      optionsJSON: opts.data.options as never,
    });
    const res = await passkeyRegisterVerifyFn({
      challengeId: opts.data.challengeId,
      response,
      playerName,
    });
    return { ok: res.data.verified === true };
  } catch (err) {
    // console, jotta syy näkyy myös etädebugatessa (chrome://inspect).
    // eslint-disable-next-line no-console
    console.warn("[passkey] rekisterointi epaonnistui:", err);
    return { ok: false, error: passkeyErrorMessage(err, "fi") ?? undefined };
  }
}

/**
 * Kirjautuu passkeyllä. Palauttaa palvelimen myöntämän tokenin ja
 * pelaajan tiedot, tai null jos tunnistautuminen ei onnistunut.
 *
 * allowCredentials on palvelimella tyhjä, joten laite tarjoaa itse
 * sopivat tunnisteet eikä klubikoodia tarvitse tietää etukäteen.
 */
export async function loginWithPasskey(): Promise<ClubSignInResult | null> {
  if (!(await passkeySupported())) return null;
  const opts = await passkeyLoginOptionsFn({});
  const response = await startAuthentication({
    optionsJSON: opts.data.options as never,
  });
  const res = await passkeyLoginVerifyFn({
    challengeId: opts.data.challengeId,
    response,
  });
  return res.data.token ? res.data : null;
}
